import { css, nil, UIRectangle, UIView, YES } from "uicore-ts"
import { CBCheckbox } from "../CB/CBCheckbox"
import { RHForms } from "./Utils_slop/RHDesignTokens"

export class RHRadioButton extends CBCheckbox {
    
    // Static flag to ensure CSS is only injected once
    private static hasInjectedStyles = false
    
    constructor(elementID?: string) {
        
        super(elementID)
        
        // 1. Set type to radio explicitly
        this.checkbox.viewHTMLElement.setAttribute("type", "radio");
        
        // 2. Container Configuration (Identical to RHCheckbox)
        (this as RHRadioButton).configureWithObject({
            style: {
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
                // padding: "4px 0"
                padding: RHForms.radioContainerPadding
            }
        })
        
        // 3. Label Styling (Identical to RHCheckbox)
        this.titleLabel.configureWithObject({
            // textColor: RHColor.textPrimary,
            textColor: RHForms.radioLabelColor,
            style: {
                // fontSize: "1rem",
                fontSize: RHForms.radioLabelFontSize,
                // fontWeight: "400",
                fontWeight: RHForms.radioLabelFontWeight,
                // marginLeft: "8px",
                marginLeft: RHForms.radioLabelMarginLeft,
                userSelect: "none"
            }
        })
        
        // 4. Glassmorphic Radio Styling (Circular)
        this.checkbox.configureWithObject({
            style: {
                appearance: "none",
                webkitAppearance: "none",
                width: RHForms.radioControlSize + "px",
                height: RHForms.radioControlSize + "px",
                borderRadius: RHForms.radioControlBorderRadius,
                backgroundColor: RHForms.radioControlBackgroundColor,
                backdropFilter: RHForms.radioControlBackdropFilter,
                //@ts-ignore
                webkitBackdropFilter: RHForms.radioControlBackdropFilter,
                cursor: "pointer",
                margin: "0",
                position: "relative",
                transition: RHForms.radioControlTransition
            }
        })
        
        this.applyGlobalStyles()
        
    }
    
    
    private applyGlobalStyles() {
        
        if (RHRadioButton.hasInjectedStyles || this.class !== RHRadioButton) {
            return
        }
        
        const classSelector = `.${this.styleClassName}`
        const inputSelector = `${classSelector} input[type="radio"]`
        
        UIView.injectCSS(css`
            ${inputSelector} {
                box-shadow: ${RHForms.radioControlShadow};
                border: ${RHForms.radioControlBorderWidth} solid ${RHForms.radioControlBorderColor};
                box-sizing: border-box;
            }

            ${inputSelector}:checked {
                background-color: ${RHForms.radioCheckedBackgroundColor} !important;
                border-color: ${RHForms.radioCheckedBorderColor} !important;
                background-image: ${RHForms.radioCheckedBackgroundImage};
                background-position: center;
                background-size: 100% 100%;
                background-repeat: no-repeat;
                box-shadow: ${RHForms.radioCheckedShadow};
            }

            ${inputSelector}:focus {
                outline: 0;
                box-shadow: ${RHForms.radioFocusShadow};
                border-color: ${RHForms.radioFocusBorderColor};
            }

            ${inputSelector}:hover:not(:checked) {
                background-color: ${RHForms.radioHoverBackgroundColor};
                border-color: ${RHForms.radioHoverBorderColor};
                box-shadow: ${RHForms.radioHoverShadow};
            }

            ${inputSelector}:active {
                filter: ${RHForms.radioActiveFilter};
            }

            ${inputSelector}:disabled {
                opacity: ${RHForms.radioDisabledOpacity};
            }
        `, `RHRadioButton_GlobalStyles`)
        
        RHRadioButton.hasInjectedStyles = true
    }
    
    
    override updateContentForCurrentEnabledState() {
        const wasHidden = this.hidden
        super.updateContentForCurrentEnabledState()
        this.hidden = wasHidden
        // Keep pointer events alive even when disabled so tooltips attached via
        // UITooltip.sharedInstance.attach() continue to receive PointerEnter/Leave.
        this.userInteractionEnabled = YES
        const opacity = this.enabled ? "1" : RHForms.radioDisabledOpacity
        this.checkbox.style.opacity = opacity
        this.titleLabel.style.opacity = opacity
        this.style.cursor = this.enabled ? "pointer" : "not-allowed"
        const inputEl = this.checkbox.viewHTMLElement as HTMLInputElement
        if (inputEl) {
            inputEl.disabled = !this.enabled
        }
    }
    
    
    override intrinsicContentWidth(constrainingHeight = 0): number {
        return Math.ceil(
            RHForms.radioControlLeadingInset + RHForms.radioControlSize +
            parseFloat(RHForms.radioLabelMarginLeft) +
            this.titleLabel.intrinsicContentWidth(constrainingHeight)
        )
    }


    override layoutSubviews() {
        const preservedTextPrefix = this.titleLabel.textPrefix
        const preservedTextSuffix = this.titleLabel.textSuffix

        super.layoutSubviews()

        // CBCheckbox uses legacy prefix/suffix clipping; RH radio labels manage
        // their own frame, and badges also use textSuffix.
        this.titleLabel.textPrefix = preservedTextPrefix
        this.titleLabel.textSuffix = preservedTextSuffix

        const bounds = this.bounds
        // Assuming 1.25em maps roughly to 20px based on standard font size context
        const radioSize = RHForms.radioControlSize
        
        bounds
            .rectangleWithHeight(radioSize)
            .rectangleWithWidth(radioSize)
            .rectangleWithX(RHForms.radioControlLeadingInset)
            .assignedAsFrameOfView(this.checkbox)
        
        const labelOffset = radioSize + RHForms.radioControlLeadingInset
        const labelWidth = bounds.width - labelOffset
        
        this.titleLabel.setMaxSizes(nil, labelWidth)
        this.titleLabel.frame = new UIRectangle(
            labelOffset,
            0,
            bounds.height,
            labelWidth
        )
        
        this.titleLabel.style.lineHeight = bounds.height + "px"
        this.titleLabel.style.height = bounds.height + "px"
    }
}
